import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, ButtonGroup, ListItem, List, Tooltip, IconButton } from '@mui/material';
import PanelItem from './PanelItem';
import './AddressPanel.css';
import httpRequest from '../../../../HttpService';
import AddressForm from '../addressForm';
import CustomInput from '../../inputFields/CustomInput';
import { useUserContext } from "../../../../lib/user_context";
import { palette } from '../../../../palette';

const placeDetailsCache = new Map();

const sxRoot = { display: 'flex', flexDirection: 'column', height: '100%' };
const sxSearchContainer = { mb: 1 };
const sxListContainer = { flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', mt: 1 };
const sxListItem = { py: 1, px: 0 };

const AddressPanel = (props) => {
    const { sessionAccountAddress } = useUserContext();
    const [googleAPILoaded, setGoogleAPILoaded] = useState(false);
    const [addrList, setAddrList] = useState([]);
    const [addrState, setAddrState] = useState(false);
    const [selectedAddrIndex, setSelectedAddrIndex] = useState(props.initialSelected);
    const [query, setQuery] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [newAddress, setNewAddress] = useState('');
    const [removedAddressIndex, setRemoveAddressIndex] = useState('');
    const [noResultsFound, setNoResultsFound] = useState(false);
    const [newAddressValues, setNewAddressValues] = useState(null);
    const [googleSearchHandler, setGoogleSearchHandler] = useState(null);
    const [googleAutocomplete, setGoogleAutocomplete] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [googleSearchResult, setGoogleSearchResult] = useState(null);
    const [googleActive, setGoogleActive] = useState(false);
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const autocompleteRef = useRef(null);
    const autocompleteListenerRef = useRef(null);

    // Debounce the search query for DB lookups (300ms)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(query);
        }, 300);
        return () => clearTimeout(timer);
    }, [query]);

    useEffect(() => {
        const loadGoogleMapScript = (callback) => {
            if (typeof window.google === 'object' && typeof window.google.maps === 'object') {
                callback();
            } else {
                const googleMapScript = document.createElement("script");
                googleMapScript.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&libraries=places`;
                window.document.body.appendChild(googleMapScript);
                googleMapScript.addEventListener("load", callback);
            }
        }
        loadGoogleMapScript(() => {
            setGoogleAPILoaded(true);
        });
    }, []);

    useEffect(() => {
        if (props.selectedAddress && addrList.length > 0 && !isSearching) {
            const currentIndex = addrList.findIndex(item => item.addressID === props.selectedAddress.addressID);
            if (currentIndex !== -1) {
                setSelectedAddrIndex(currentIndex);
            }
        }
    }, [props.selectedAddress, addrList, isSearching]);

    const refreshAddressEXT = (address) => {
        if (!address?.googlePlaceID || !window.google?.maps?.places) return;
        if (placeDetailsCache.has(address.googlePlaceID)) return;

        const service = new window.google.maps.places.PlacesService(document.createElement('div'));
        service.getDetails(
            {
                placeId: address.googlePlaceID,
                fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id', 'current_opening_hours', 'utc_offset_minutes']
            },
            (place, status) => {
                if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;
                placeDetailsCache.set(address.googlePlaceID, true);
                const serializablePlace = {
                    address_components: place.address_components,
                    formatted_address: place.formatted_address,
                    geometry: { location: { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() } },
                    name: place.name,
                    place_id: place.place_id,
                    utc_offset_minutes: place.utc_offset_minutes,
                    current_opening_hours: place.current_opening_hours
                        ? { weekday_text: place.current_opening_hours.weekday_text }
                        : undefined
                };
                httpRequest.postAddressEXT(address.addressID, {
                    json: JSON.stringify(serializablePlace),
                    place_id: place.place_id
                });
            }
        );
    };

    const handleListItemClick = (index) => {
        setSelectedAddrIndex(index);
        setIsSearching(false);
        const address = addrList[index];
        props.onChange(address, props.type);
        refreshAddressEXT(address);
    };

    const handleSearch = (event) => {
        const searchValue = event.target.value;
        setQuery(searchValue);
        // Keep isSearching true once the user starts typing, until they select an item
        if (searchValue.length > 0 || isSearching) {
            setIsSearching(true);
        }
        setSelectedAddrIndex("");
        setNoResultsFound(false);
    }

    const handleClick = (event) => {
        setShowForm(!showForm);
        setNoResultsFound(false);
        setIsSearching(false);
    };

    function handleNewAddress(newAddress) {
        setNewAddress(newAddress);
        console.log('New Address added', newAddress);
        setAddrState(addrList.unshift(newAddress));
        setSelectedAddrIndex(0);
        props.onChange(newAddress, props.type);
    }

    function handleRemoveAddress(removedAddressIndex) {
        setRemoveAddressIndex(removedAddressIndex);
        console.log('Removed Address - index', removedAddressIndex);
        setAddrState(addrList.splice(removedAddressIndex, 1));
        setSelectedAddrIndex('');
    }

    // This function handles updates from a PanelItem and updates the master list
    function handleAddressUpdateInList(index, updatedAddress) {
        const newAddrList = [...addrList];
        newAddrList[index] = updatedAddress;
        setAddrList(newAddrList);

        // If the updated address is the currently selected one, inform the parent component.
        if (selectedAddrIndex === index) {
            props.onChange(updatedAddress, props.type);
        }
    }

    useEffect(() => {
        setNoResultsFound(false);

        async function getAddr() {
            if(!props.accountNumber) return;
            const addressList = [];
            httpRequest.getTopAddressByType(props.accountNumber, {$: debouncedQuery}, props.type).then(async function(body){
                var userAddress = sessionAccountAddress;
                var defaultAddress = null;
                if(userAddress !== ""){
                    defaultAddress = JSON.parse(userAddress);
                }
                await body.data.forEach((value) => {
                    if(defaultAddress==null || value.addressID !== defaultAddress.addressID )
                        addressList.push(value);
                });
                if(defaultAddress && debouncedQuery.length == 0)
                    addressList.unshift(defaultAddress);

                if(addressList.length > 0) {
                    let listWithID =  addressList.map((value,index) => {
                        value.id = index;
                        return value;
                    });
                    setAddrList(listWithID);

                    // Only auto-select first shipper if no address is already selected from quote
                    // props.selectedAddress comes from quote data when loading a quote
                    if (props.type === "shipper" && debouncedQuery === "" && !props.selectedAddress?.addressID) {
                        setSelectedAddrIndex(0);
                        props.onChange(listWithID[0], props.type, true);
                    } else {
                        setSelectedAddrIndex("");
                    }

                    setAddrState(true);
                } else {
                    if (debouncedQuery.length > 4) {
                        expandSearchAddr();
                    } else {
                        setAddrList([]);
                        setAddrState(false);
                        if (debouncedQuery) {
                            setNoResultsFound(true);
                            setGoogleActive(true);
                        }
                    }
                }
            });
        }

        async function expandSearchAddr() {
            if(!props.accountNumber) return;
            const expandedSearchAddressList = [];
            httpRequest.getExpandedAddress(props.accountNumber,{$: debouncedQuery}, 0).then(async function(body){
                await body.data.forEach((value) => {
                    expandedSearchAddressList.push(value);
                });

                if(expandedSearchAddressList.length > 0) {
                    await setAddrList(expandedSearchAddressList);
                    setAddrState(true);
                } else {
                    setAddrList([]);
                    setAddrState(false);
                    setNoResultsFound(true);
                    if (debouncedQuery) setGoogleActive(true);
                }
            });
        }

        if(!showForm && sessionAccountAddress !== null) {
            getAddr();
        }
    },[sessionAccountAddress, props.accountNumber, props.quoteId, debouncedQuery]);

    // Detach any existing Google Autocomplete widget and its pac-container dropdown
    const detachAutocomplete = () => {
        if (autocompleteListenerRef.current) {
            window.google.maps.event.removeListener(autocompleteListenerRef.current);
            autocompleteListenerRef.current = null;
        }
        if (autocompleteRef.current) {
            autocompleteRef.current.unbindAll();
            autocompleteRef.current = null;
        }
        // Remove the pac-container dropdown that Google appends to <body>
        document.querySelectorAll('.pac-container').forEach(el => el.remove());
    };

    // Attach Google Places Autocomplete when user clicks the Google button
    useEffect(() => {
        if (!googleActive || !googleAPILoaded || !props.inputRef?.current) return;

        // Clean up any previous widget first
        detachAutocomplete();

        try {
            let geolocation = { lat: 51.04522, lng: -114.06302 };

            if (sessionAccountAddress) {
                const defaultAddress = JSON.parse(sessionAccountAddress);
                if (defaultAddress?.lat && defaultAddress?.long) {
                    geolocation = { lat: defaultAddress.lat, lng: defaultAddress.long };
                }
            }

            const circle = new window.google.maps.Circle({ center: geolocation, radius: 5000 });
            const options = {
                bounds: circle.getBounds(),
                fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id', 'current_opening_hours']
            };

            const inputEl = props.inputRef.current;
            autocompleteRef.current = new window.google.maps.places.Autocomplete(inputEl, options);
            autocompleteListenerRef.current = autocompleteRef.current.addListener("place_changed", function() {
                const place = autocompleteRef.current.getPlace();
                const placeFormatted = convertPlaceToFriendlyObject(place);
                populateMatchedAddress(placeFormatted);
                setShowForm(true);
                setGoogleActive(false);
            });

            // Force the widget to show predictions immediately for current text.
            // Google's Autocomplete widget triggers on keydown events with printable keyCodes.
            // We simulate pressing the last character of the current value to wake it up.
            inputEl.focus();
            setTimeout(() => {
                const lastChar = inputEl.value.slice(-1) || 'a';
                inputEl.dispatchEvent(new KeyboardEvent('keydown', {
                    key: lastChar,
                    keyCode: lastChar.charCodeAt(0),
                    bubbles: true
                }));
            }, 150);
        } catch (error) {
            console.error("Error setting up Google Places:", error);
            setGoogleActive(false);
        }

        return () => {
            detachAutocomplete();
        };
    }, [googleActive, googleAPILoaded]);

    function convertPlaceToFriendlyObject(place) {
        let result = {};
        if(place && place.address_components) {
            let components = place.address_components;

            for( let i = 0; i < components.length; i++ ) {
                if (i === 0) {
                    result.searchBy = components[i].types[0];
                }
                result[components[i].types[0]] = components[i].long_name;
            }
         }
        if (place) {
            result.formattedAddress = place.formatted_address;
            result.lat = place.geometry?.location?.lat();
            result.lng = place.geometry?.location?.lng();
            result.name = place.name;
            result.place_id = place.place_id;
            result.utc_offset_minutes = place.utc_offset_minutes;
        }

        // Store the Google search result for post_addressExt
        // Create a serializable version of the place object
        try {
            const serializablePlace = {
                address_components: place?.address_components,
                formatted_address: place?.formatted_address,
                geometry: {
                    location: {
                        lat: result.lat,
                        lng: result.lng
                    }
                },
                name: place?.name,
                place_id: place?.place_id,
                utc_offset_minutes: place?.utc_offset_minutes,
                opening_hours: place?.current_opening_hours
                    ? { weekday_text: place.current_opening_hours.weekday_text }
                    : undefined
            };
            setGoogleSearchResult({
                "json": JSON.stringify(serializablePlace),
                "place_id": result.place_id
            });
        } catch (e) {
            console.error("Error storing Google search result:", e);
        }

        return result;
   }

    function populateMatchedAddress (myAddress) {
        let tempAddressValues = {};
        if(myAddress.route !== undefined){
            if(myAddress.route.endsWith("Southwest")) {
                tempAddressValues.quad = "SW";
                tempAddressValues.address = myAddress.street_number + ' ' + myAddress.route.substring(0, myAddress.route.indexOf("Southwest"));
            }
            else if(myAddress.route.endsWith("Southeast")) {
                tempAddressValues.quad = "SE";
                tempAddressValues.address = myAddress.street_number + ' ' + myAddress.route.substring(0, myAddress.route.indexOf("Southeast"));
            }
            else if(myAddress.route.endsWith("Northwest")) {
                tempAddressValues.quad = "NW";
                tempAddressValues.address = myAddress.street_number + ' ' + myAddress.route.substring(0, myAddress.route.indexOf("Northwest"));
            }
            else if(myAddress.route.endsWith("Northeast")) {
                tempAddressValues.quad = "NE";
                tempAddressValues.address = myAddress.street_number + ' ' + myAddress.route.substring(0, myAddress.route.indexOf("Northeast"));
            }
            else {
                tempAddressValues.quad = "";
                tempAddressValues.address = myAddress.street_number + ' ' + myAddress.route;
            }
        }
        else {
            let arr = myAddress.formattedAddress.split(',');
            if(arr[0].includes('Southwest')) {
                tempAddressValues.quad = "SW";
                tempAddressValues.address = arr[0].substring(0,arr[0].indexOf('Southwest'));
            }
            else if(arr[0].includes('Southeast')) {
                tempAddressValues.quad = "SE";
                tempAddressValues.address = arr[0].substring(0,arr[0].indexOf('Southeast'));
            }
            else if(arr[0].includes('Northwest')) {
                tempAddressValues.quad = "NW";
                tempAddressValues.address = arr[0].substring(0,arr[0].indexOf('Northwest'));
            }
            else if(arr[0].includes('Northeast')) {
                tempAddressValues.quad = "NE";
                tempAddressValues.address = arr[0].substring(0,arr[0].indexOf('Northeast'));
            }
            else {
                tempAddressValues.quad = '';
                tempAddressValues.address = arr[0];
            }
        }

        tempAddressValues.city = myAddress.locality;
        tempAddressValues.country = myAddress.country;
        tempAddressValues.postal = myAddress.postal_code;
        tempAddressValues.lat = myAddress.lat;
        tempAddressValues.long = myAddress.lng;
        tempAddressValues.prov =  myAddress.administrative_area_level_1;
        tempAddressValues.companyName = myAddress.name;

        // Dispatch event for the map to zoom to the new pin
        const mapUpdateEvent = new CustomEvent('addressSelected', {
            detail: {
                lat: myAddress.lat,
                lng: myAddress.lng
            }
        });
        window.dispatchEvent(mapUpdateEvent);

        setNewAddressValues(tempAddressValues);
    }

    const predictionsList = () => {
        return (
            <>
            </>
        );
    }

    const listItems = data => {
        return data.map((item, index) => (
            <ListItem
                button
                className="panel-list-item"
                sx={sxListItem}
                divider={true}
                disabled={props.pickUpLocation ? props.pickUpLocation.addressID === item.addressID : false}
                selected={selectedAddrIndex === index}
                autoFocus={selectedAddrIndex === index}
                onClick={() => {
                    handleListItemClick(index);
                    setNewAddressValues(null);
                }}
                key={`address-list-item-${index}`}
            >
                <PanelItem
                    selected={selectedAddrIndex === index}
                    data={item}
                    index={index}
                    type={props.type}
                    key={`address-panel-item-${index}`}
                    onChange={props.onChange}
                    removedAddressIndex={handleRemoveAddress}
                    onChangeContact={props.onChangeContact}
                    accountNumber={props.accountNumber}
                    onAddressUpdate={handleAddressUpdateInList}
                />
            </ListItem>
        ));
    };

    const handleGoogleSearch = () => {
        setGoogleActive(!googleActive);
    };

    const GoogleGIcon = () => (
        <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
    );

    const googleButton = (
        <Tooltip title="Search Google Places" placement="top" arrow>
            <IconButton
                onClick={handleGoogleSearch}
                size="small"
                style={{
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    minWidth: '40px',
                    width: '40px',
                    height: '100%',
                    backgroundColor: googleActive ? palette.primary.dark : palette.greys.white,
                    color: googleActive ? palette.primary.contrastText : palette.primary.main,
                }}
            >
                <GoogleGIcon />
            </IconButton>
        </Tooltip>
    );

    return (
        <Box sx={sxRoot}>
            <Box sx={sxSearchContainer}>
                <Tooltip title="Select the delivery address or type" placement="top" arrow>
                    <Box>
                        <CustomInput
                            type="name"
                            name={props.type === "consignee" ? "DeliverySearch:" : "PickupSearch"}
                            icon="AddOutlinedIcon"
                            inputType="text"
                            label={props.type === "consignee" ? "Delivery Location:" : "Pickup Location"}
                            onIconClick={handleClick}
                            labelSize={5}
                            onChange={handleSearch}
                            inputRef={props.inputRef}
                            value={query}
                            extraButton={googleButton}
                        />
                        {predictionsList()}
                    </Box>
                </Tooltip>
            </Box>

            {showForm && (
                <AddressForm
                    accountNumber={props.accountNumber}
                    title="New Address"
                    saveButtonLabel="Save"
                    cancelButtonLabel="Cancel"
                    variant="add"
                    cancelButtonOnClick={() => {
                        handleClick();
                        setNewAddressValues(null);
                        setGoogleSearchResult(null);
                        if (props.inputRef && props.inputRef.current) {
                           props.inputRef.current.value = '';
                        }
                        setQuery("");
                        setIsSearching(false);
                        // Dispatch event to clear the temporary marker from the map
                        window.dispatchEvent(new CustomEvent('clearPreviewMarker'));
                    }}
                    afterSaveAddress={() => {
                        handleClick();
                        setNewAddressValues(null);
                        setGoogleSearchResult(null);
                    }}
                    newAddress={handleNewAddress}
                    data={newAddressValues}
                    initialAddressSearchResult={googleSearchResult}
                />
            )}

            {addrState && noResultsFound && (
                <Box sx={{ padding: '15px', textAlign: 'center', color: '#888' }}>
                    No addresses found. Try a different search or add a new address.
                </Box>
            )}

            {addrState && !noResultsFound && (
                <Box className="panel-list" sx={sxListContainer} id="addr-form">
                    <List className="panel-list-container" disablePadding={true} key="addr-list">
                        {listItems(addrList)}
                    </List>
                </Box>
            )}
        </Box>
    );
}

export default AddressPanel;